import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { Campaign, OmniCampaign } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';
import type { IUniversalSelectOption } from '@feathr/components';
import {
  Alert,
  ButtonValid,
  CardV2 as Card,
  Form,
  Input,
  UniversalSelect,
} from '@feathr/components';
import { useAccount, useLocalUrl, useUser } from '@feathr/extender/state';

import ChannelsCard from './ChannelsCard';
import MetaChannelSettingsCard from './MetaChannelSettingsCard';

import * as styles from '../AdWizard/AdWizard.css';

interface IProps {
  onNext: () => void;
  campaign: Campaign;
  /** Whether any Display creatives exist on the campaign */
  hasDisplayCreatives?: boolean;
  /** Whether any Meta creatives exist on the campaign */
  hasMetaCreatives?: boolean;
}

export interface IValidateStepGeneralConfig {
  isOmniCampaign?: boolean;
}

export function validateStepGeneral(
  campaign: Campaign,
  config?: IValidateStepGeneralConfig,
): string[] {
  const { isOmniCampaign } = config ?? {};

  if (isOmniCampaign) {
    return campaign.validate(['name', 'integrations', 'meta_settings'], false).errors;
  }

  return campaign.validate(['name'], false).errors;
}

const NextStepButton = observer(({ campaign, onNext }: IProps) => {
  const { t } = useTranslation();
  const { isOmniCampaign } = campaign;
  const validationErrors = validateStepGeneral(campaign, {
    isOmniCampaign,
  });
  return (
    <ButtonValid errors={validationErrors} name={'next_step'} onClick={onNext}>
      {t('Next')}
    </ButtonValid>
  );
});

function WizardGeneralStep({
  campaign,
  onNext,
  hasDisplayCreatives,
  hasMetaCreatives,
}: Readonly<IProps>): JSX.Element {
  const account = useAccount();
  const user = useUser();
  const localUrl = useLocalUrl();
  const { t } = useTranslation();
  const { isDraft, isMonetization, isOmniCampaign, isTTDCampaign } = campaign;

  const metaSelected =
    isOmniCampaign &&
    (campaign as OmniCampaign).get('integrations')?.includes(EIntegrationTypes.Meta);

  const displaySelected =
    isOmniCampaign &&
    (campaign as OmniCampaign).get('integrations')?.includes(EIntegrationTypes.Tradedesk);

  const advOptions: IUniversalSelectOption[] = (account.get('ttd')?.subadvertisers || []).map(
    (advertiser) => ({
      id: advertiser.subadv_id,
      name: advertiser.name || '',
    }),
  );

  const selectedAdvertiser = advOptions.find(({ id }) => id === campaign.get('subadvertiser'));

  // Non-Omni TTD campaigns are always Display; Omni campaigns require Display to be selected
  const hasDisplayChannel = !isOmniCampaign || displaySelected;

  const showAdvertiserSelect =
    !isMonetization &&
    isTTDCampaign &&
    hasDisplayChannel &&
    (account.get('is_agency') || user.isSudoer);

  function handleSelectAdvertiser(option: IUniversalSelectOption): void {
    campaign.set({ subadvertiser: option.id });
  }

  function handleClearAdvertiser(): void {
    campaign.set({ subadvertiser: undefined });
  }

  return (
    <Form
      actions={
        // TODO: Remove this when we horizontal wizard the campaign types that also utilize this step
        !isTTDCampaign && [<NextStepButton campaign={campaign} key={'next'} onNext={onNext} />]
      }
      className={classNames({ [styles.formRoot]: isTTDCampaign })}
      label={'Edit campaign: Name'}
    >
      <Card>
        <Card.Header
          description={t('Provide a descriptive name for your campaign so you can find it later.')}
          title={t('Name')}
        />
        <Card.Content>
          <Input
            attribute={'name'}
            label={'Campaign name'}
            model={campaign}
            required={true}
            type={'text'}
          />
        </Card.Content>
      </Card>
      {isOmniCampaign && (
        <ChannelsCard
          campaign={campaign as OmniCampaign}
          hasDisplayCreatives={hasDisplayCreatives}
          hasMetaCreatives={hasMetaCreatives}
        />
      )}
      {isOmniCampaign && metaSelected && (
        <MetaChannelSettingsCard campaign={campaign as OmniCampaign} />
      )}
      {showAdvertiserSelect && (
        <Card>
          <Card.Header
            description={
              <Trans t={t}>
                Add organizations to this list on the{' '}
                <Link to={localUrl('settings/account/advertisers')}>advertisers page</Link> in your
                account settings.{' '}
                <a
                  href={'https://help.feathr.co/hc/en-us/articles/4410253428375'}
                  target={'_blank'}
                >
                  Learn about advertisers
                </a>
              </Trans>
            }
            title={t('Advertiser')}
          />
          <Card.Content addVerticalGap={true}>
            <Alert
              description={t('This setting cannot be changed after the campaign is published.')}
              title={t(
                'If this campaign is to advertise on behalf of another organization, please choose that organization here.',
              )}
              type={'warning'}
            />

            <UniversalSelect<IUniversalSelectOption>
              disabled={!isDraft}
              isClearable={isDraft}
              label={t('Advertiser')}
              name={'advertiser'}
              onClear={handleClearAdvertiser}
              onSelectSingle={handleSelectAdvertiser}
              options={advOptions}
              value={selectedAdvertiser}
            />
          </Card.Content>
        </Card>
      )}
    </Form>
  );
}

export default observer(WizardGeneralStep);
